import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerFooter,
} from '@/components/ui/drawer';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CheckCircle2, Inbox, Trash2, Eye } from 'lucide-react';

/**
 * kind: 'volunteers' | 'contacts' | 'donations'
 * columns / renderDetail: how to render list rows and detail drawer
 */
export const InboxManager = ({ kind, title, description, columns, renderDetail }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminApi.listSubmissions(kind, {});
            setItems(data);
        } catch {
            toast.error('Failed to load submissions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind]);

    const toggleRead = async (row) => {
        try {
            await adminApi.markRead(kind, row.id, !row.is_read);
            toast.success(row.is_read ? 'Marked unread' : 'Marked as read');
            setSelected((cur) =>
                cur && cur.id === row.id ? { ...cur, is_read: !cur.is_read } : cur
            );
            await load();
        } catch {
            toast.error('Update failed');
        }
    };

    const remove = async () => {
        if (!confirmDelete) return;
        try {
            await adminApi.deleteSubmission(kind, confirmDelete.id);
            toast.success('Deleted');
            setConfirmDelete(null);
            if (selected?.id === confirmDelete.id) setSelected(null);
            await load();
        } catch {
            toast.error('Delete failed');
        }
    };

    return (
        <div data-testid={`admin-inbox-${kind}`}>
            <div className="mb-6">
                <h1 className="font-display text-2xl font-semibold">{title}</h1>
                {description && (
                    <p className="text-sm text-muted-foreground">{description}</p>
                )}
            </div>
            <Card className="bg-card border-border">
                <CardContent className="p-4">
                    {loading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-16">
                            <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                            <div className="font-display font-semibold">
                                No submissions yet
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Public submissions will appear here.
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-4"></TableHead>
                                        {columns.map((c) => (
                                            <TableHead key={c.key}>{c.label}</TableHead>
                                        ))}
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {items.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            className={row.is_read ? 'opacity-70' : ''}
                                        >
                                            <TableCell>
                                                {!row.is_read && (
                                                    <span className="h-2 w-2 rounded-full bg-[hsl(var(--accent))] inline-block" />
                                                )}
                                            </TableCell>
                                            {columns.map((c) => (
                                                <TableCell
                                                    key={c.key}
                                                    className={
                                                        c.primary
                                                            ? 'font-medium max-w-[220px] truncate'
                                                            : ''
                                                    }
                                                >
                                                    {c.render ? c.render(row) : row[c.key]}
                                                </TableCell>
                                            ))}
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setSelected(row)}
                                                    data-testid={`admin-${kind}-view-${row.id}`}
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => toggleRead(row)}
                                                    data-testid={`admin-${kind}-read-${row.id}`}
                                                >
                                                    <CheckCircle2
                                                        className={`h-4 w-4 ${
                                                            row.is_read
                                                                ? 'text-[hsl(var(--accent))]'
                                                                : ''
                                                        }`}
                                                    />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setConfirmDelete(row)}
                                                    className="text-destructive hover:text-destructive"
                                                    data-testid={`admin-${kind}-delete-${row.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DrawerContent className="max-h-[92vh]">
                    <DrawerHeader>
                        <DrawerTitle>Submission details</DrawerTitle>
                    </DrawerHeader>
                    <div className="px-6 pb-4 overflow-y-auto space-y-3 max-h-[68vh]">
                        {selected && renderDetail(selected)}
                    </div>
                    <DrawerFooter className="flex-row justify-end gap-2">
                        {selected && (
                            <Button
                                variant="outline"
                                onClick={() => toggleRead(selected)}
                                data-testid="admin-inbox-toggle-read"
                            >
                                {selected.is_read ? 'Mark as Unread' : 'Mark as Read'}
                            </Button>
                        )}
                        <Button onClick={() => setSelected(null)}>Close</Button>
                    </DrawerFooter>
                </DrawerContent>
            </Drawer>

            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={remove}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid={`admin-${kind}-delete-confirm`}
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default InboxManager;
